/**
 * List Contacts Handler
 * Query handler for listing contacts from Supabase using GraphQL
 */

import type { ToolExecutionContext } from '../../types';
import { QueryHandlerBase } from '../base';
import { numberField } from '../../utils/schema-builder';
import { createUserGraphQLClient } from '../../utils/graphql-client';

/**
 * List contacts handler
 * Uses GraphQL queries to list contacts with RLS enforcement
 */
export class ListContactsHandler extends QueryHandlerBase<any> {
  id = 'list_contacts';

  schema = {
    type: 'object',
    properties: {
      limit: numberField('Maximum number of contacts to return (default: 20, max: 100)', { minimum: 1, maximum: 100 }),
      orderBy: {
        type: 'string',
        enum: ['created_at', 'updated_at', 'name'],
        description: 'Field to order results by (default: created_at)',
      },
    },
    required: [],
  };

  async execute(params: Record<string, any>, context?: ToolExecutionContext): Promise<any> {
    const limit = Math.min(params.limit || 20, 100);
    const orderBy = params.orderBy || 'created_at';

    if (!context?.userToken) {
      throw new Error('User token is required for contact listing');
    }

    try {
      // Dynamically import GraphQL types and documents to avoid circular dependencies
      const { GetContactssDocument } = await import('@/src/gql/graphql');
      
      // Create user-scoped GraphQL client that respects RLS
      const client = createUserGraphQLClient(context.userToken);

      // Query contacts with ordering
      const result = await client.query({
        query: GetContactssDocument,
        variables: {
          first: limit,
          orderBy: [{ [orderBy]: 'DescNullsLast' }],
        } as Parameters<typeof client.query>[0]['variables'],
        fetchPolicy: 'network-only',
      });

      if (result.error) {
        throw new Error(`GraphQL error: ${result.error.message}`);
      }

      const data = result.data as any;
      const contacts = data?.contactsCollection?.edges
        ?.map((edge: any) => edge?.node)
        .filter((node: any) => node != null) || [];

      if (contacts.length === 0) {
        return {
          results: [],
          total: 0,
          message: 'No contacts found',
        };
      }

      const viewInAppLinks = contacts.map((c: any) => `[${c.name || 'Contact'}](/contacts/${c.id})`).join(' · ');
      return {
        results: contacts.map((c: any) => ({ ...c, viewInApp: `[View Contact](/contacts/${c.id})` })),
        total: contacts.length,
        limit,
        orderBy,
        viewInApp: viewInAppLinks,
      };
    } catch (error) {
      throw new Error(`Contact listing failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}
